import { useState, useMemo, useEffect } from 'react'
import { useApplication } from '../../../context/application'
import {
  ApplicationBodyContainer, 
  ApplicationBodyContent,
  DefaultTitle, 
  DefaultSubtitle,
} from '../../../components/application/defaultComponents'
import ApplicationPrebuiltFooter from '../../../components/application/PrebuiltBodyFooter'
import PercentagesForm from 'src/components/application/mainPagesComponents/Firm/PercentagesForm'


interface AreasWithPercentage {
  area: string;  //group
  type?: string; 
  percentage: number;
  code?: string; // ISI code for this specific area+type combo.
}

const Percentages = () => {
  const { application } = useApplication()
  const [showAlert, setShowAlert] = useState(false)
  const [areasWithPercentage, setAreasWithPercentage] = useState<AreasWithPercentage[]>(() => {
    if(!application?.firmSection?.practiceAreas) return []

    let data: AreasWithPercentage[] = []
    
    application.firmSection.practiceAreas.forEach(area => {
      if(area.types && area.types.length > 0) {
        data = [...data, ...area.types.map(sub => ({ area: area.area ?? '', type: sub.type, percentage: sub.percentage ?? 0 }))]
      } else {
        data = [...data, { area: area.area ?? '', percentage: area.percentage ?? 0 }]
      }
    })

    return data
  })

  const totalPercentage = useMemo(() => {
    return areasWithPercentage.reduce((total, value) => parseFloat((total += value.percentage).toFixed(2)), 0)
  }, [areasWithPercentage])
  
  const handleSliderChange = (value: number, index: number) => {
    setAreasWithPercentage(prev => {
      const updated = [...prev]
      updated[index].percentage = value
      return updated
    })
  }

  const handleInputChange = (value: number, index: number) => {
    let approxValue = parseFloat(((value ?? 0)/10).toFixed(2));
    setAreasWithPercentage(prev => {
      const updated = [...prev]
      updated[index].percentage = approxValue
      return updated
    })
  }

  const buildPercentageData = () => {
    if(application?.firmSection?.practiceAreas) {
      const practiceAreas = [...application.firmSection.practiceAreas]

      return practiceAreas.map(pArea => {
        if(pArea?.types && pArea?.types.length > 0) {
          return {
            ...pArea,
            types: [
              ...pArea.types.map(tp => ({ ...tp, percentage: areasWithPercentage.find(ar => ar.type === tp.type)?.percentage ?? 0 }))
            ]
          }
        } else {
          return {
            ...pArea,
            percentage: areasWithPercentage.find(ar => ar.area === pArea.area)?.percentage ?? 0
          }
        }
      })
    }
    return []
  }

  const goNextRoute = () => {
    const realEstate = application.firmSection?.practiceAreas?.find(a => a.area === 'Real Estate')
    if (realEstate !== undefined && realEstate.types !== undefined && realEstate.types.length > 0) {
      return '/firm/real-estate'
    }

    const sec1 = application.firmSection?.practiceAreas?.find(a => a.area === 'Securities Municipal Bonds - Tax Exempt')
    const sec2 = application.firmSection?.practiceAreas?.find(a => a.area === 'Securities - Other Types Of Securities Practice')

    if((sec1 || sec2) ){
      return '/firm/securities-law'
    }
    
    return '/firm/relationships'
  }

  useEffect(() => {
    if(totalPercentage !== 100 || areasWithPercentage.some(ar => ar.percentage === 0)) {
      setShowAlert(true)
    }
    else setShowAlert(false)
  }, [areasWithPercentage, totalPercentage])

  const validatePercentages = () => {
    const hasBuiltDataElements = buildPercentageData().length > 0

    const isPercentagesCorrectlyFilled = totalPercentage === 100 && areasWithPercentage.every(ar => ar.percentage > 0)

    return hasBuiltDataElements && isPercentagesCorrectlyFilled
  }

  const updatedApplicationData = () => ({
    ...application,
    currentPage: window.location.pathname,
    firmSection: { ...application.firmSection, practiceAreas: buildPercentageData() } 
  })
  
  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width="94%">
        <DefaultTitle>Add percentages to your areas of practice.</DefaultTitle>
        <DefaultSubtitle>
          The total percentage must add up to 100%.
        </DefaultSubtitle>
        <PercentagesForm
          areasWithPercentage={areasWithPercentage}
          handleSliderChange={handleSliderChange}
          handleInputChange={handleInputChange}
          showAlert={showAlert}
          totalPercentage={totalPercentage}
        />
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/firm/practice"
        isFormValid={validatePercentages()}
        nextRoute={goNextRoute()}
        updatedApplication={updatedApplicationData()}
      />
    </ApplicationBodyContainer>
  )
}

export default Percentages